import { Fragment, useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { format, parseISO } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table/data-table';
import { Skeleton } from '@/components/ui/skeleton';
import { TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { analyticsV2Service } from '@/services/analyticsV2.service';
import { useAccountBusinessUnits } from '@/hooks/use-business-units';
import {
  useCostBreakdown,
  useCostFeatures,
  useCostSeries,
  useCostSummary,
  useCostTickets,
  useCostTicketTypes,
  useCostWeekly,
} from '@/hooks/use-analytics-v2';
import type {
  AnalyticsV2QueryParams,
  CostBreakdownGroupBy,
  CostBreakdownRow,
  CostCategoryKey,
  CostExportType,
  CostFeatureCategory,
  CostGranularity,
  CostQueryParams,
  CostSeriesGroupBy,
  CostSummary,
  CostTicketFilter,
  CostTicketRow,
  CostWeeklyLevel,
} from '@/types/analyticsV2';
import { MetricCard } from './metric-card';
import { CostTicketDialog } from './cost-ticket-dialog';
import { CostFilters } from './cost-filters';
import {
  AREA_COLORS,
  buildCostChart,
  CATEGORY_LABEL,
  costHealthParts,
  costKeyLabel,
  formatCount,
  formatRatio,
  formatShare,
  formatUnitCost,
  formatUsd,
  granularityForRange,
  groupWeeklyRows,
  GRANULARITY_LABEL,
  MAX_CALLS_EXPORT_DAYS,
  pruneCostFilter,
  rangeDays,
  weekLabel,
  type CostWeeklyTotals,
} from '../cost-format';

interface CostSectionProps {
  fromDate: string;
  toDate: string;
  accountId: number;
  /** Brand to scope to, or null for the whole account. */
  buId?: string | null;
}

const TICKETS_PAGE_SIZE = 20;
const SERIES_COLORS = ['#7C3AED', '#2563EB', '#0D9488', '#D97706', '#DB2777', '#65A30D', '#9CA3AF'];

const SERIES_GROUP_BYS: { value: CostSeriesGroupBy; label: string }[] = [
  { value: 'area', label: 'Area' },
  { value: 'feature', label: 'Feature' },
  { value: 'service', label: 'Service' },
  { value: 'model', label: 'Model' },
];
const BREAKDOWN_GROUP_BYS: { value: CostBreakdownGroupBy; label: string }[] = [
  { value: 'feature', label: 'Feature' },
  { value: 'model', label: 'Model' },
  { value: 'service', label: 'Service' },
  { value: 'provider', label: 'Provider' },
  { value: 'brand', label: 'Brand' },
];

const periodLabel = (period: string, granularity: CostGranularity): string => {
  if (granularity === 'year') return period;
  return format(parseISO(period), granularity === 'day' ? 'MMM d' : 'MMM yy');
};

const isEmpty = (s: CostSummary) => s.totalCostUsd === 0 && s.llmCalls === 0 && s.apiCalls === 0;

/** Small segmented toggle, the same control the dashboard tabs use. */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as T)} variant='segmented'>
      <TabsList aria-label={label}>
        {options.map((o) => (
          <TabsTrigger key={o.value} value={o.value}>
            {o.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function SectionShell({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <h2 className='text-[12px] text-gray-800 font-medium'>{title}</h2>
        {actions}
      </div>
      {children}
    </div>
  );
}

const TableSkeleton = () => (
  <div className='flex flex-col gap-2 p-4'>
    <Skeleton className='h-8 w-full rounded-md' />
    <Skeleton className='h-8 w-full rounded-md' />
    <Skeleton className='h-8 w-full rounded-md' />
  </div>
);

/**
 * AI & API spend for one account (or brand) from the AI cost ledger. The health line reports
 * on the ledger itself and is company-wide except for unpriced calls.
 */
export function CostSection(props: CostSectionProps) {
  // A cleared date picker: nothing to load, and the downloads would only 400.
  if (!props.fromDate || !props.toDate) return <EmptyBox message='Pick a date range to see AI cost' />;
  return <CostSectionForRange {...props} />;
}

function CostSectionForRange({ fromDate, toDate, accountId, buId }: CostSectionProps) {
  const range = useMemo<AnalyticsV2QueryParams>(
    () => ({ fromDate, toDate, accountId, buId }),
    [fromDate, toDate, accountId, buId],
  );
  const ticketTypes = useCostTicketTypes(range);
  const [filter, setFilter] = useState<CostTicketFilter>({});
  // A new brand or range keeps the picks it still offers; any it no longer lists go back to All.
  if (ticketTypes.data && !ticketTypes.isLoading) {
    const pruned = pruneCostFilter(filter, ticketTypes.data);
    if (pruned !== filter) setFilter(pruned);
  }
  const params = useMemo<CostQueryParams>(() => ({ ...range, ...filter }), [range, filter]);
  const summary = useCostSummary(params);
  const units = useAccountBusinessUnits(accountId);
  const brandName = useCallback(
    (id: string) => units?.find((u) => String(u.id) === id)?.name,
    [units],
  );
  const [ticketId, setTicketId] = useState<number | null>(null);
  // Shared by the weekly table and both weekly CSV buttons, so a download matches what is shown.
  const [weeklyLevel, setWeeklyLevel] = useState<CostWeeklyLevel>('ticket');

  const s = summary.data;
  const filtered = Boolean(s?.ticketFilterApplied);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-1'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h2 className='text-[12px] text-gray-800 font-medium'>AI &amp; API cost</h2>
          <CostExportControl params={params} weeklyLevel={weeklyLevel} />
        </div>
        <CostFilters types={ticketTypes.data} filter={filter} onChange={setFilter} />
        {filtered ? (
          <p className='text-xs text-[#5f656f]' data-testid='cost-filter-note'>
            Showing costs tied to matching tickets only — overhead such as SOP builds and insights jobs is excluded.
          </p>
        ) : null}
        {summary.isLoading ? (
          <Skeleton className='h-4 w-2/3 rounded-md' />
        ) : s ? (
          <p className='text-xs text-[#5f656f] tabular-nums' data-testid='cost-health'>
            {costHealthParts(s.health).join(' · ')}
          </p>
        ) : null}
      </div>

      {summary.isError ? (
        <EmptyBox message='Failed to load AI cost.' />
      ) : s && isEmpty(s) ? (
        <EmptyBox message='No AI usage recorded for this period yet' />
      ) : (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            <MetricCard
              title='Total AI & API spend'
              description={s ? `${formatCount(s.llmCalls)} LLM · ${formatCount(s.apiCalls)} API calls` : 'LLM and API calls in range'}
              value={s ? formatUsd(s.totalCostUsd) : null}
              isLoading={summary.isLoading || !s}
            />
            <MetricCard
              title='Avg cost per ticket'
              description={
                s && !filtered ? `Fully loaded ${formatUsd(s.fullyLoadedCostPerTicket)} incl. overhead` : 'Direct cost of tickets started in range'
              }
              value={s ? formatUsd(s.directCostPerTicket) : null}
              isLoading={summary.isLoading || !s}
            />
            <MetricCard
              title='Avg cost per response'
              description={s ? `${formatCount(s.responses)} responses` : 'Ticket cost / AI responses'}
              value={s ? formatUsd(s.costPerResponse) : null}
              isLoading={summary.isLoading || !s}
            />
            <MetricCard
              title='Avg responses per ticket'
              description={s ? `${formatCount(s.tickets)} tickets` : 'AI responses / tickets'}
              value={s ? formatRatio(s.avgResponsesPerTicket) : null}
              isLoading={summary.isLoading || !s}
            />
          </div>
          <CostChartCard params={params} brandName={brandName} />
          <FeatureAreas params={params} brandName={brandName} />
          <WeeklyReport params={params} level={weeklyLevel} onLevelChange={setWeeklyLevel} />
          {/* Keyed so a new range, brand or filter starts again at the first page. */}
          <TicketsTable
            key={`${accountId}|${fromDate}|${toDate}|${buId ?? 'all'}|${filter.channel ?? ''}|${filter.category ?? ''}|${filter.intent ?? ''}`}
            params={params}
            brandName={brandName}
            onOpen={setTicketId}
          />
        </>
      )}

      <CostTicketDialog accountId={accountId} buId={buId} conversationId={ticketId} onClose={() => setTicketId(null)} />
    </div>
  );
}

const EXPORT_OPTIONS: { value: CostExportType; label: string }[] = [
  { value: 'tickets', label: 'Tickets' },
  { value: 'daily', label: 'Daily by feature' },
  { value: 'weekly', label: 'Weekly brand report' },
  { value: 'responses', label: 'Responses' },
  { value: 'calls', label: 'All calls' },
];
/** Raw-row exports kim-analytics caps at MAX_CALLS_EXPORT_DAYS. */
const RANGE_LIMITED: ReadonlySet<CostExportType> = new Set(['calls', 'responses']);

/** Hands a blob to the browser as a file download. */
const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** One CSV download at a time; failures toast. */
function useCostDownload(params: CostQueryParams, weeklyLevel: CostWeeklyLevel) {
  const [pending, setPending] = useState<CostExportType | null>(null);
  const download = async (type: CostExportType) => {
    setPending(type);
    try {
      const { blob, filename } = await analyticsV2Service.downloadCostExport(params, type, weeklyLevel);
      saveBlob(blob, filename);
    } catch (error) {
      console.error('[CostExport] download failed', { type, error });
      toast.error('Could not download the CSV. Please try again.');
    } finally {
      setPending(null);
    }
  };
  return { pending, download };
}

/** "Download CSV: Tickets | … | All calls". Responses and All calls are raw rows, so capped at 31 days. */
export function CostExportControl({ params, weeklyLevel }: { params: CostQueryParams; weeklyLevel: CostWeeklyLevel }) {
  const { pending, download } = useCostDownload(params, weeklyLevel);
  const tooLong = rangeDays(params.fromDate, params.toDate) > MAX_CALLS_EXPORT_DAYS;

  return (
    <div role='group' aria-label='Download CSV' className='flex flex-wrap items-center gap-1.5 text-xs text-[#5f656f]'>
      <span aria-hidden='true'>Download CSV:</span>
      {EXPORT_OPTIONS.map((o) => {
        const disabledByRange = RANGE_LIMITED.has(o.value) && tooLong;
        const button = (
          <Button
            key={o.value}
            variant='outline'
            size='sm'
            aria-label={`Download ${o.label.toLowerCase()} CSV`}
            aria-busy={pending === o.value}
            disabled={pending !== null || disabledByRange}
            onClick={() => download(o.value)}
          >
            {pending === o.value ? 'Preparing…' : o.label}
          </Button>
        );
        if (!disabledByRange) return button;
        // A disabled button fires no pointer events, so the tooltip hangs off a focusable wrapper.
        const tipId = `cost-export-${o.value}-limit`;
        return (
          <Tooltip key={o.value}>
            <TooltipTrigger asChild>
              <span tabIndex={0} aria-describedby={tipId}>
                {button}
              </span>
            </TooltipTrigger>
            <TooltipContent id={tipId}>
              {o.label} is limited to {MAX_CALLS_EXPORT_DAYS} days. Pick a shorter range.
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function EmptyBox({ message }: { message: string }) {
  return (
    <div className='rounded-3xl border border-gray-300 bg-white p-12 text-center text-sm text-grey-500'>{message}</div>
  );
}

interface PartProps {
  params: CostQueryParams;
  brandName: (buId: string) => string | undefined;
}

function CostChartCard({ params, brandName }: PartProps) {
  const granularity = granularityForRange(params.fromDate, params.toDate);
  const [groupBy, setGroupBy] = useState<CostSeriesGroupBy>('area');
  const { data, isLoading, isError } = useCostSeries(params, granularity, groupBy);
  const byArea = groupBy === 'area';

  const chart = useMemo(() => {
    // Area points name themselves; every area gets its own series (there are few, and colours are fixed).
    const labels = new Map((data?.points ?? []).map((p) => [p.key, p.label]));
    return buildCostChart(
      data?.points ?? [],
      (k) => labels.get(k) ?? costKeyLabel(groupBy, k, brandName),
      byArea ? Infinity : undefined,
    );
  }, [data, groupBy, byArea, brandName]);
  const colorOf = (series: (typeof chart.series)[number], i: number) =>
    byArea
      ? (AREA_COLORS[series.key as CostCategoryKey] ?? AREA_COLORS.other)
      : SERIES_COLORS[i % SERIES_COLORS.length];
  // The response says which granularity it is, so labels never mismatch while the next one loads.
  const rows = useMemo(
    () => chart.rows.map((r) => ({ ...r, label: periodLabel(String(r.period), data?.granularity ?? granularity) })),
    [chart.rows, data?.granularity, granularity],
  );

  return (
    <SectionShell
      title='Spend over time'
      actions={
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-[#5f656f]' data-testid='cost-granularity'>
            {GRANULARITY_LABEL[granularity]}
          </span>
          <Segmented label='Group by' value={groupBy} options={SERIES_GROUP_BYS} onChange={setGroupBy} />
        </div>
      }
    >
      <div className='rounded-3xl border border-grey-200 bg-white p-5 flex flex-col gap-4 h-[380px]'>
        <div className='flex-1 min-h-0 w-full'>
          {isLoading ? (
            <Skeleton className='h-full w-full rounded-md' />
          ) : rows.length === 0 ? (
            <div className='h-full w-full flex items-center justify-center text-sm text-grey-500'>
              {isError ? 'Failed to load spend over time.' : 'No data for the selected period.'}
            </div>
          ) : (
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' vertical={false} />
                <XAxis dataKey='label' tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => formatUsd(v)}
                  width={72}
                />
                <ChartTooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                  formatter={(value: number) => formatUsd(value)}
                />
                {chart.series.map((series, i) => (
                  <Bar
                    key={series.dataKey}
                    dataKey={series.dataKey}
                    name={series.label}
                    stackId='cost'
                    fill={colorOf(series, i)}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {!isLoading && rows.length > 0 ? (
          <div className='flex items-center gap-4 flex-wrap shrink-0'>
            {chart.series.map((series, i) => (
              <div key={series.dataKey} className='flex items-center gap-1.5 text-xs text-grey-700'>
                <span className='inline-block h-2.5 w-2.5 rounded-full' style={{ backgroundColor: colorOf(series, i) }} />
                {series.label}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}

const num = (value: string) => <span className='tabular-nums'>{value}</span>;

function BreakdownTable({ params, brandName }: PartProps) {
  const [groupBy, setGroupBy] = useState<CostBreakdownGroupBy>('feature');
  // A picked brand makes "by brand" a single row, so the option only exists for all brands.
  const brandScoped = Boolean(params.buId);
  if (brandScoped && groupBy === 'brand') setGroupBy('feature');
  const options = brandScoped ? BREAKDOWN_GROUP_BYS.filter((g) => g.value !== 'brand') : BREAKDOWN_GROUP_BYS;
  const { data, isLoading, isError } = useCostBreakdown(params, brandScoped && groupBy === 'brand' ? 'feature' : groupBy);

  const columns = useMemo<ColumnDef<CostBreakdownRow>[]>(
    () => [
      { id: 'key', header: BREAKDOWN_GROUP_BYS.find((g) => g.value === groupBy)?.label, size: 220, cell: ({ row }) => costKeyLabel(groupBy, row.original.key, brandName) },
      { accessorKey: 'costUsd', header: 'Cost', size: 120, cell: ({ row }) => num(formatUsd(row.original.costUsd)) },
      { accessorKey: 'share', header: 'Share', size: 90, cell: ({ row }) => num(formatShare(row.original.share)) },
      { accessorKey: 'events', header: 'Calls', size: 100, cell: ({ row }) => num(formatCount(row.original.events)) },
      { accessorKey: 'inputTokens', header: 'Input tokens', size: 130, cell: ({ row }) => num(formatCount(row.original.inputTokens)) },
      { accessorKey: 'outputTokens', header: 'Output tokens', size: 130, cell: ({ row }) => num(formatCount(row.original.outputTokens)) },
    ],
    [groupBy, brandName],
  );

  return (
    <SectionShell
      title='Detailed breakdown'
      actions={<Segmented label='Break down by' value={groupBy} options={options} onChange={setGroupBy} />}
    >
      <div className='rounded-3xl border border-gray-300 bg-white p-2'>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            columns={columns}
            data={data?.groupBy === groupBy ? data.rows : []}
            enableSorting
            enablePagination={false}
            emptyMessage={isError ? 'Failed to load the breakdown.' : 'No AI usage recorded for this period yet'}
          />
        )}
      </div>
    </SectionShell>
  );
}

/** Row disclosure: a native button, so Enter and Space work; the chevron turns when open. */
function ExpandButton({
  expanded,
  controls,
  onToggle,
  children,
}: {
  expanded: boolean;
  controls: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type='button'
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={onToggle}
      className='inline-flex items-center gap-1.5 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/40'
    >
      <ChevronRight
        aria-hidden='true'
        className={cn('h-3.5 w-3.5 shrink-0 text-grey-500 transition-transform', expanded && 'rotate-90')}
      />
      {children}
    </button>
  );
}

/** Open/closed set of row keys. */
function useExpanded() {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  return { isOpen: (key: string) => open.has(key), toggle };
}

function ShareBar({ share }: { share: number }) {
  return (
    <div className='flex items-center gap-2'>
      <div aria-hidden='true' className='h-1.5 flex-1 min-w-16 overflow-hidden rounded-full bg-gray-100'>
        <div className='h-full rounded-full bg-[#7C3AED]' style={{ width: `${Math.min(100, Math.max(0, share * 100))}%` }} />
      </div>
      <span className='w-12 text-right tabular-nums'>{formatShare(share)}</span>
    </div>
  );
}

const numCell = (value: string) => <TableCell className='text-right tabular-nums'>{value}</TableCell>;

/** Spend by feature area, each expandable into its raw features; the raw breakdown sits behind a toggle. */
function FeatureAreas({ params, brandName }: PartProps) {
  const { data, isLoading, isError } = useCostFeatures(params);
  const { isOpen, toggle } = useExpanded();
  const [detailed, setDetailed] = useState(false);
  const baseId = useId();
  const unmapped = data?.unmappedFeatures.length ?? 0;

  return (
    <div className='flex flex-col gap-6'>
      <SectionShell
        title='Cost by feature'
        actions={
          <Button
            variant='ghost'
            size='sm'
            aria-expanded={detailed}
            aria-controls={`${baseId}-detailed`}
            onClick={() => setDetailed(!detailed)}
          >
            {detailed ? 'Hide detailed breakdown' : 'Show detailed breakdown'}
          </Button>
        }
      >
        <div className='rounded-3xl border border-gray-300 bg-white p-2'>
          {isLoading ? (
            <TableSkeleton />
          ) : !data || data.categories.length === 0 ? (
            <p className='py-10 text-center text-sm text-grey-500'>
              {isError ? 'Failed to load cost by feature.' : 'No AI usage recorded for this period yet'}
            </p>
          ) : (
            <>
              <div className='overflow-x-auto'>
                <table className='w-full min-w-[720px] text-sm'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Area</TableHead>
                      <TableHead className='text-right'>Cost</TableHead>
                      <TableHead className='w-[200px]'>Share</TableHead>
                      <TableHead>Per unit</TableHead>
                      <TableHead className='text-right'>Calls</TableHead>
                    </TableRow>
                  </TableHeader>
                  {data.categories.map((c: CostFeatureCategory) => {
                    const id = `${baseId}-${c.key}`;
                    const expanded = isOpen(c.key);
                    return (
                      <Fragment key={c.key}>
                        <tbody>
                          <TableRow>
                            <TableCell className='font-medium text-[#2b2e34]'>
                              <ExpandButton expanded={expanded} controls={id} onToggle={() => toggle(c.key)}>
                                {c.label}
                              </ExpandButton>
                            </TableCell>
                            {numCell(formatUsd(c.costUsd))}
                            <TableCell>
                              <ShareBar share={c.share} />
                            </TableCell>
                            <TableCell className='text-[#5f656f] tabular-nums'>{formatUnitCost(c)}</TableCell>
                            {numCell(formatCount(c.events))}
                          </TableRow>
                        </tbody>
                        {expanded ? (
                          <tbody id={id} className='bg-gray-50/60'>
                            {c.features.map((f) => (
                              <TableRow key={f.key} className='text-[#5f656f]'>
                                <TableCell className='pl-8'>{costKeyLabel('feature', f.key, brandName)}</TableCell>
                                {numCell(formatUsd(f.costUsd))}
                                <TableCell />
                                <TableCell />
                                {numCell(formatCount(f.events))}
                              </TableRow>
                            ))}
                          </tbody>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </table>
              </div>
              {unmapped > 0 ? (
                <p className='px-3 pb-1 pt-2 text-xs text-[#5f656f]' title={data.unmappedFeatures.join(', ')}>
                  {formatCount(unmapped)} {unmapped === 1 ? 'feature' : 'features'} not yet mapped to an area — shown under Other
                </p>
              ) : null}
            </>
          )}
        </div>
      </SectionShell>
      {detailed ? (
        <div id={`${baseId}-detailed`}>
          <BreakdownTable params={params} brandName={brandName} />
        </div>
      ) : null}
    </div>
  );
}

const WEEKLY_LEVELS: { value: CostWeeklyLevel; label: string }[] = [
  { value: 'ticket', label: 'Tickets' },
  { value: 'response', label: 'Responses' },
];

/** Non-zero category costs, largest first. */
function CategoryCosts({ categories }: { categories: CostWeeklyTotals['categories'] }) {
  const entries = (Object.entries(categories) as [CostCategoryKey, number][])
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <span className='text-grey-500'>No category costs this week.</span>;
  return (
    <ul className='flex flex-wrap gap-x-5 gap-y-1'>
      {entries.map(([k, v]) => (
        <li key={k}>
          {CATEGORY_LABEL[k] ?? k} <span className='tabular-nums text-[#2b2e34]'>{formatUsd(v)}</span>
        </li>
      ))}
    </ul>
  );
}

const WEEKLY_COLUMNS = 8;

function WeeklyRow({
  id,
  label,
  totals,
  expanded,
  onToggle,
  subtotal = false,
  indent = false,
}: {
  id: string;
  label: string;
  totals: CostWeeklyTotals;
  expanded: boolean;
  onToggle: () => void;
  subtotal?: boolean;
  indent?: boolean;
}) {
  return (
    <>
      <TableRow className={cn(subtotal && 'bg-gray-50/60 font-medium text-[#2b2e34]')} data-testid={subtotal ? 'weekly-subtotal' : 'weekly-row'}>
        <TableCell className={cn('whitespace-nowrap', indent && 'pl-8')}>
          <ExpandButton expanded={expanded} controls={id} onToggle={onToggle}>
            {label}
          </ExpandButton>
        </TableCell>
        {numCell(formatCount(totals.tickets))}
        {numCell(formatCount(totals.responses))}
        {numCell(formatRatio(totals.responsesPerTicket))}
        {numCell(formatUsd(totals.ticketCostUsd))}
        {numCell(formatUsd(totals.avgTicketCostUsd))}
        {numCell(formatUsd(totals.avgResponseCostUsd))}
        {numCell(formatUsd(totals.totalCostUsd))}
      </TableRow>
      {expanded ? (
        <TableRow id={id} className='hover:bg-transparent'>
          <TableCell colSpan={WEEKLY_COLUMNS} className={cn('text-xs text-[#5f656f]', indent ? 'pl-14' : 'pl-8')}>
            <CategoryCosts categories={totals.categories} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/**
 * Brand × week report. For all brands, rows are grouped by brand under a subtotal row;
 * for one brand, just its weeks.
 */
function WeeklyReport({
  params,
  level,
  onLevelChange,
}: {
  params: CostQueryParams;
  level: CostWeeklyLevel;
  onLevelChange: (level: CostWeeklyLevel) => void;
}) {
  const { data, isLoading, isError } = useCostWeekly(params, level);
  const { pending, download } = useCostDownload(params, level);
  const { isOpen, toggle } = useExpanded();
  const baseId = useId();
  const allBrands = !params.buId;
  // The hook keeps the previous level's rows while the next loads; never show them under the new switch.
  const groups = useMemo(() => (data?.level === level ? groupWeeklyRows(data.rows) : []), [data, level]);

  const weekRows = (rows: (typeof groups)[number]['rows'], indent: boolean) =>
    rows.map((r) => {
      const key = `${r.buId ?? 'account'}-${r.year}-${r.week}`;
      return (
        <WeeklyRow
          key={key}
          id={`${baseId}-${key}`}
          label={weekLabel(r)}
          totals={r}
          expanded={isOpen(key)}
          onToggle={() => toggle(key)}
          indent={indent}
        />
      );
    });

  return (
    <SectionShell
      title='Weekly report'
      actions={
        <div className='flex flex-wrap items-center gap-2'>
          <Segmented label='Weekly report by' value={level} options={WEEKLY_LEVELS} onChange={onLevelChange} />
          <Button
            variant='outline'
            size='sm'
            aria-busy={pending !== null}
            disabled={pending !== null}
            onClick={() => download('weekly')}
          >
            {pending !== null ? 'Preparing…' : 'Download weekly CSV'}
          </Button>
        </div>
      }
    >
      <div className='rounded-3xl border border-gray-300 bg-white p-2'>
        {isLoading ? (
          <TableSkeleton />
        ) : groups.length === 0 ? (
          <p className='py-10 text-center text-sm text-grey-500'>
            {isError ? 'Failed to load the weekly report.' : 'No AI usage recorded for this period yet'}
          </p>
        ) : (
          <div className='max-h-[520px] overflow-auto'>
            <table className='w-full min-w-[920px] text-sm'>
              <TableHeader className='sticky top-0 z-10'>
                <TableRow>
                  <TableHead>{allBrands ? 'Brand / week' : 'Week'}</TableHead>
                  <TableHead className='text-right'>Tickets</TableHead>
                  <TableHead className='text-right'>Responses</TableHead>
                  <TableHead className='text-right'>Responses/ticket</TableHead>
                  <TableHead className='text-right'>Ticket cost</TableHead>
                  <TableHead className='text-right'>Avg/ticket</TableHead>
                  <TableHead className='text-right'>Avg/response</TableHead>
                  <TableHead className='text-right'>Total cost</TableHead>
                </TableRow>
              </TableHeader>
              {allBrands ? (
                groups.map((g) => {
                  const key = `${g.buId ?? 'account'}-total`;
                  return (
                    <tbody key={key}>
                      <WeeklyRow
                        id={`${baseId}-${key}`}
                        label={g.brandName}
                        totals={g.subtotal}
                        expanded={isOpen(key)}
                        onToggle={() => toggle(key)}
                        subtotal
                      />
                      {weekRows(g.rows, true)}
                    </tbody>
                  );
                })
              ) : (
                <tbody>{weekRows(groups.flatMap((g) => g.rows), false)}</tbody>
              )}
            </table>
          </div>
        )}
      </div>
    </SectionShell>
  );
}

function TicketsTable({ params, brandName, onOpen }: PartProps & { onOpen: (conversationId: number) => void }) {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError } = useCostTickets(params, TICKETS_PAGE_SIZE, offset);

  const columns = useMemo<ColumnDef<CostTicketRow>[]>(
    () => [
      {
        accessorKey: 'conversationId',
        header: 'Conversation',
        size: 140,
        // A real button so keyboard users can open the drill-down; the row click is for the mouse.
        cell: ({ row }) => (
          <button
            type='button'
            className='tabular-nums text-[#7C3AED] underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none'
            aria-label={`View cost breakdown for conversation ${row.original.conversationId}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row.original.conversationId);
            }}
          >
            {row.original.conversationId}
          </button>
        ),
      },
      {
        id: 'brand',
        header: 'Brand',
        size: 160,
        cell: ({ row }) => costKeyLabel('brand', row.original.buId === null ? null : String(row.original.buId), brandName),
      },
      { accessorKey: 'responses', header: 'Responses', size: 100, cell: ({ row }) => num(formatCount(row.original.responses)) },
      { accessorKey: 'llmCalls', header: 'LLM calls', size: 100, cell: ({ row }) => num(formatCount(row.original.llmCalls)) },
      { accessorKey: 'apiCalls', header: 'API calls', size: 100, cell: ({ row }) => num(formatCount(row.original.apiCalls)) },
      { accessorKey: 'costUsd', header: 'Cost', size: 120, cell: ({ row }) => num(formatUsd(row.original.costUsd)) },
    ],
    [brandName, onOpen],
  );

  // Last known total, so a failed later page keeps the pager (and Previous) instead of
  // stranding the user on that offset.
  const [total, setTotal] = useState(0);
  if (data && data.total !== total) setTotal(data.total);
  const pageEnd = Math.min(offset + TICKETS_PAGE_SIZE, total);

  return (
    <SectionShell title='Most expensive tickets'>
      <div className='rounded-3xl border border-gray-300 bg-white p-2'>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            columns={columns}
            data={data?.rows ?? []}
            enablePagination={false}
            onRowClick={(row) => onOpen(row.conversationId)}
            emptyMessage={isError ? 'Failed to load tickets.' : 'No tickets with AI usage in this period.'}
          />
        )}
        {total > TICKETS_PAGE_SIZE ? (
          <div className='flex items-center justify-end gap-2 px-3 py-2 text-xs text-[#5f656f] tabular-nums'>
            <span>
              {formatCount(offset + 1)}–{formatCount(pageEnd)} of {formatCount(total)}
            </span>
            <Button
              variant='outline'
              size='sm'
              disabled={offset === 0 || isLoading}
              onClick={() => setOffset(Math.max(0, offset - TICKETS_PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant='outline'
              size='sm'
              disabled={pageEnd >= total || isLoading}
              onClick={() => setOffset(offset + TICKETS_PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}
